import * as actions from './../../actions/index';

import {
  CreateTemplatedDeviceDto,
  Device,
  DeviceCreateUpdateBody,
  DeviceFilterAttributes,
  DeviceModel,
  Pagination,
} from '@thingslog/repositories';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { AxiosError } from 'axios';
import Button from '@mui/material/Button';
import { GaEventAction } from '../../common/GaEventAction';
import { GaEventCategory } from '../../common/GaEventCategory';
import GoogleAnalyticsService from '../../common/GoogleAnalyticsService';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import InventoryModal from './InventoryModal';
import InventoryTable from './InventoryTable';
import RmaHistoryModal from './RmaHistoryModal';
import { ReduxState } from '../../reducers';
import {
  devicesQueryClient,
  linkDevicesQueryClient,
  iconsQueryClient,
  deviceModelsQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import { useDispatch, useSelector } from 'react-redux';
import {
  GridColumnVisibilityModel,
  GridRowId,
  GridToolbar,
  useModal,
  useToast,
} from '@thingslog/ui-components';
import { useTranslation } from 'react-i18next';
import JwtValidator from '../../common/JwtValidator';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmDeleteOrUnlinkModal from './ConfirmDeleteUnlinkDevice';
import { QueryKeys } from '@thingslog/queries/src/enums/QueryKeys';
import { CreateOrUpdateDeviceProps } from './models/CreateOrUpdateDeviceProps';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

type DeleteOrUnlinkModalData = {
  selectedDevice: string;
};

const Inventory: FC<InventoryProps> = () => {
  const dispatch = useDispatch();
  const { modal, closeModal } = useModal();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    useDevicesData,
    useDeleteDevice,
    useCreateDevice,
    useUpdateDevice,
    useCreateDeviceFromTemplate,
  } = useMemo(() => devicesQueryClient, []);
  const { useGetIcons } = useMemo(() => iconsQueryClient, []);
  const { useUnlinkDevice, useLinkDevice } = useMemo(() => linkDevicesQueryClient, []);
  const { useDeviceModelsData } = useMemo(() => deviceModelsQueryClient, []);
  const { hasRole, decodedToken } = useMemo(() => new JwtValidator(), []);

  const [attributes, setAttributes] = useState<DeviceFilterAttributes | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [filterValue, setFilterValue] = useState<string>('');
  const [page, setPage] = useState(0);
  const [queryAttributes, setQueryAttributes] = useState<DeviceFilterAttributes | null>(null);
  const [queryFilterValue, setQueryFilterValue] = useState<string | null>(null);
  const [size, setSize] = useState(10);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    createdBy: !!hasRole('ROLE_SUPER_ADMIN'),
  });
  const [selectedDeviceToEdit, setSelectedDeviceToEdit] = useState<string | null>(null);
  const [createOrUpdateDeviceData, setCreateOrUpdateDeviceData] =
    useState<CreateOrUpdateDeviceProps | null>(null);
  const [deleteOrUnlinkModalData, setDeleteOrUnlinkModalData] =
    useState<DeleteOrUnlinkModalData | null>(null);
  const companyId = useSelector((state: ReduxState) => state.company.id);
  const companyIdToken = decodedToken?.companyId;
  const selectedDevice = useSelector((state: ReduxState) => state.dev.devices);

  const showUnlinkBtn = useMemo(
    () => hasRole('ROLE_SUPER_ADMIN') && companyId !== null,
    [hasRole, companyId]
  );

  const determineCompanyIdForLinkingDevice = useMemo(() => {
    if (hasRole('ROLE_SUPER_ADMIN')) {
      if (companyId) {
        return companyId;
      }
    } else if (hasRole('ROLE_ADMIN')) {
      return Number(companyIdToken);
    }
    return null;
  }, [companyId, companyIdToken]);

  useEffect(() => {
    const timeoutFilterChange = setTimeout(() => setQueryFilterValue(filterValue), 1000);
    return (): void => clearTimeout(timeoutFilterChange);
  }, [filterValue]);

  useEffect(() => {
    const timeoutAttributesChange = setTimeout(() => setQueryAttributes(attributes), 1000);
    return (): void => clearTimeout(timeoutAttributesChange);
  }, [attributes]);

  const getDevicesQuery = useDevicesData(companyId, page, size, queryAttributes, queryFilterValue, {
    onSuccess: (data: Pagination<Device>) => {
      setDevices(data.content);
    },
    onError: () => {
      toast({
        type: 'error',
        message: t('devices_table_error'),
      });
      closeModal();
    },
  });

  const devicesDeleteQuery = useDeleteDevice({
    onSuccess: () => {
      dispatch(actions.devSelected([], 'radio'));
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({
        type: 'success',
        message: t('device_inventory_delete_device_success'),
      });
      setDeleteOrUnlinkModalData(null);
    },
    onError: (error: AxiosError) => {
      toast({
        type: 'error',
        message: t('device_inventory_delete_device_error'),
      });
    },
  });

  const unlinkDeviceQuery = useUnlinkDevice({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({
        type: 'success',
        message: t('device_inventory_unlink_device_success'),
      });
      setDeleteOrUnlinkModalData(null);
    },
    onError: (error: AxiosError) => {
      toast({
        type: 'error',
        message: t('device_inventory_unlink_device_error'),
      });
    },
  });

  const createDeviceQuery = useCreateDevice({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({
        type: 'success',
        message: t('device_inventory_create_device_success'),
      });
    },
    onError: (error: AxiosError) => {
      if (error.response?.data.errorCode === 'DEVICE_ALREADY_EXISTS') {
        toast({
          type: 'error',
          message: t('device_inventory_already_exists_error'),
        });
      } else {
        toast({
          type: 'error',
          message: t('device_inventory_create_device_error'),
        });
      }
    },
  });

  const updateDeviceQuery = useUpdateDevice({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({
        type: 'success',
        message: t('device_inventory_update_device_success'),
      });
      closeModal();
    },
    onError: () => {
      toast({
        type: 'error',
        message: t('device_inventory_update_device_error'),
      });
    },
  });

  const createDeviceFromTemplateQuery = useCreateDeviceFromTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.UseDeviceData]);
      toast({
        type: 'success',
        message: t('device_inventory_create_device_success'),
      });
      closeModal();
    },
    onError: (error: AxiosError) => {
      if (error.response?.data.errorCode === 'DEVICE_ALREADY_EXISTS') {
        toast({
          type: 'error',
          message: t('device_inventory_already_exists_error'),
        });
      } else {
        toast({
          type: 'error',
          message: t('device_inventory_create_device_error'),
        });
      }
    },
  });

  const iconsQuery = useGetIcons(selectedDeviceToEdit || null, null, companyId, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const deviceModelsQuery = useDeviceModelsData();

  const filteredDeviceModels = (deviceModels: DeviceModel[]): DeviceModel[] =>
    hasRole('ROLE_SUPER_ADMIN')
      ? deviceModels
      : deviceModels.filter((model: DeviceModel) =>
          [DeviceModel.MODBUS_SLAVE, DeviceModel.TASMOTA].includes(model)
        );

  useEffect(() => {
    iconsQuery.refetch();
  }, [selectedDeviceToEdit]);

  useEffect(() => {
    if (createOrUpdateDeviceData && iconsQuery.data) {
      handleOpenModal(createOrUpdateDeviceData);
    }
  }, [iconsQuery.data, createOrUpdateDeviceData]);

  const handleOpenModal = (createOrUpdateDeviceData: CreateOrUpdateDeviceProps): void => {
    const modalTitle =
      createOrUpdateDeviceData.type === 'UPDATE'
        ? t('device_inventory_update_device_title', {
            number: createOrUpdateDeviceData.deviceNumber,
          })
        : t('device_inventory_add_device');
    modal({
      title: modalTitle,
      content: (
        <div className="md:w-[400px] overflow-y-auto max-h-[80vh]">
          <InventoryModal
            createOrUpdateDeviceData={createOrUpdateDeviceData}
            devices={devices}
            onCreateDeviceManual={(payload: DeviceCreateUpdateBody): void => {
              payload.ownerCompanyId = determineCompanyIdForLinkingDevice;
              createDeviceQuery.mutate(payload);
            }}
            onUpdateDeviceManual={(deviceNumber: string, payload: DeviceCreateUpdateBody): void => {
              updateDeviceQuery.mutate({ deviceNumber, body: payload });
            }}
            onCreateTemplate={(payload: CreateTemplatedDeviceDto): void => {
              payload.ownerCompanyId = determineCompanyIdForLinkingDevice;
              createDeviceFromTemplateQuery.mutate(payload);
            }}
            iconsDropdownOptions={iconsQuery.data?.icons || []}
            deviceModelsDropdownOptions={filteredDeviceModels(deviceModelsQuery.data || [])}
            canEditDeviceModel={hasRole('ROLE_SUPER_ADMIN')}
            canEditWarranty={hasRole('ROLE_SUPER_ADMIN')}
            deviceNumberPrefix={
              companyIdToken && Number(companyIdToken) > 1 ? companyIdToken : undefined
            }
          />
        </div>
      ),
    });
  };

  const handleCreateOrUpdateDevice = (
    createOrUpdateDeviceData: CreateOrUpdateDeviceProps
  ): void => {
    setCreateOrUpdateDeviceData(createOrUpdateDeviceData);
    if (createOrUpdateDeviceData.type === 'UPDATE') {
      setSelectedDeviceToEdit(createOrUpdateDeviceData.deviceNumber);
    } else {
      setSelectedDeviceToEdit(null);
    }
  };

  const handleDeleteOrUnlinkDevice = (
    selectedDevice: string,
    companyId: number | null,
    showUnlinkBtn: boolean
  ): void => {
    setDeleteOrUnlinkModalData({
      selectedDevice,
    });
  };

  const handleUnlinkDevice = (): void => {
    if (!companyId || !deleteOrUnlinkModalData) return;

    unlinkDeviceQuery.mutate({
      companyId,
      deviceNumber: deleteOrUnlinkModalData.selectedDevice,
      removeForSubAccounts: true,
    });
  };

  const canManageRma = hasRole('ROLE_SUPER_ADMIN') || hasRole('ROLE_ADMIN');

  const handleOpenRmaModal = (deviceNumber: string): void => {
    const device = devices.find((d: Device) => d.number === deviceNumber);
    if (!device) return;
    modal({
      title: t('device_inventory_rma_history_title', { number: deviceNumber }),
      content: (
        <div className="md:w-[640px] overflow-y-auto max-h-[85vh]">
          <RmaHistoryModal device={device} canAdd={canManageRma} />
        </div>
      ),
    });
  };

  const shouldShowDeleteIcons = (deviceModel: DeviceModel): boolean => {
    if (hasRole('ROLE_SUPER_ADMIN')) {
      return true;
    }
    if (hasRole('ROLE_ADMIN')) {
      if (deviceModel == DeviceModel.MODBUS_SLAVE) {
        return true;
      }
    }
    return false;
  };

  return (
    <Header>
      <TitleHeader title={t('device_inventory_page_header')} />
      <section className="flex justify-end flex-wrap">
        <div className="flex space-x-2 max-lg:mt-4 max-md:w-full">
          {(hasRole('ROLE_SUPER_ADMIN') || hasRole('ROLE_ADMIN')) && (
            <Button
              variant="contained"
              className="max-md:w-full max-md:mt-4"
              onClick={(): void => handleCreateOrUpdateDevice({ type: 'CREATE' })}
            >
              {t('device_inventory_add_device')}
            </Button>
          )}
        </div>
      </section>

      <div className="my-5">
        <InventoryTable
          devices={devices}
          onDeleteOrUnlinkDevice={handleDeleteOrUnlinkDevice}
          onUpdateDevice={handleCreateOrUpdateDevice}
          onRmaHistory={handleOpenRmaModal}
          showRmaHistory={canManageRma}
          companyId={companyId}
          showUnlinkBtn={hasRole('ROLE_SUPER_ADMIN') && companyId !== null}
          showDeleteIcons={shouldShowDeleteIcons}
          rowsPerPageOptions={[10, 25, 50, 100]}
          rowCount={getDevicesQuery.data ? getDevicesQuery.data.totalElements : 0}
          pageSize={size}
          onPageChange={(page: number): void => {
            setPage(page);
          }}
          onPageSizeChange={(pageSize: number): void => {
            setSize(pageSize);
            setPage(0);
          }}
          components={{ Toolbar: GridToolbar }}
          autoHeight={true}
          page={page}
          paginationMode="server"
          filterMode="server"
          disableSelectionOnClick={false}
          checkboxSelection
          headerHeight={120}
          disableColumnMenu={true}
          disableColumnFilter={true}
          setAttributes={setAttributes}
          setFilterValue={setFilterValue}
          filterValue={filterValue}
          keepNonExistentRowsSelected={true}
          selectionModel={selectedDevice}
          onSelectionModelChange={(selectedRowId: GridRowId[]): void => {
            if (selectedRowId.length > 0) {
              const lastSelectedDevice = selectedRowId[selectedRowId.length - 1];
              dispatch(actions.devSelected([lastSelectedDevice], 'radio'));
              GoogleAnalyticsService.triggerEvent(
                GaEventCategory.INVENTORY_PAGE,
                GaEventAction.INVENTORY_PAGE_DEVICE_NUMBER_CLICK,
                String(lastSelectedDevice)
              );
            } else {
              dispatch(actions.devSelected([], 'radio'));
            }
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(columnModel: GridColumnVisibilityModel): void => {
            setColumnVisibilityModel({
              ...columnModel,
              createdBy: !!hasRole('ROLE_SUPER_ADMIN'),
            });
          }}
        />
      </div>

      <Dialog
        open={deleteOrUnlinkModalData !== null}
        onClose={(): void => setDeleteOrUnlinkModalData(null)}
      >
        <DialogTitle>
          {deleteOrUnlinkModalData &&
            (showUnlinkBtn
              ? t('device_inventory_modal_select_action', {
                  number: deleteOrUnlinkModalData.selectedDevice,
                })
              : t('device_inventory_modal_delete_device_title', {
                  number: deleteOrUnlinkModalData.selectedDevice,
                }))}
        </DialogTitle>
        <DialogContent>
          {deleteOrUnlinkModalData && (
            <ConfirmDeleteOrUnlinkModal
              onDeleteDevice={(): void =>
                devicesDeleteQuery.mutate(deleteOrUnlinkModalData.selectedDevice)
              }
              showUnlinkBtn={showUnlinkBtn}
              onUnlinkDevice={companyId && showUnlinkBtn ? handleUnlinkDevice : undefined}
              isLoading={devicesDeleteQuery.isLoading || unlinkDeviceQuery.isLoading}
            />
          )}
        </DialogContent>
      </Dialog>
    </Header>
  );
};

interface InventoryProps {}

export default Inventory;
